import { Loader2, Paperclip } from 'lucide-react';
import { Badge } from '../ui/badge';
import { openDocumentView } from '../../utils/openDocumentView';

interface DocumentsTabContentProps {
  appointment: any;
  documents: any[];
  poDocuments: any[];
  uploading: boolean;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, fileType: string) => void;
  formatTime: (dateStr?: string) => string;
}

export function DocumentsTabContent({
  appointment,
  documents,
  poDocuments,
  uploading,
  onFileUpload,
  formatTime,
}: DocumentsTabContentProps) {
  return (
    <div className="space-y-6">

      {/* Pre-arrival upload banner */}
      {appointment && ['pending', 'approved', 'confirmed', 'requested'].includes(appointment.status) && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/8 border border-blue-500/20 text-sm">
          <svg className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold text-blue-300 leading-tight">Pre-arrival documents welcome</p>
            <p className="text-blue-400/80 text-xs mt-0.5">
              Attach BOLs, carrier agreements, or customs docs now so your team is ready before the truck arrives.
              Documents are visible to warehouse staff immediately.
            </p>
          </div>
        </div>
      )}

      {/* PO Documents section — from po_documents, not appointment-specific */}
      {(poDocuments?.length ?? 0) > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">PO Documents</h4>
            <span className="text-xs bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full border border-slate-200 dark:border-white/10 font-medium">Warehouse only</span>
          </div>
          <div className="space-y-2">
            {poDocuments.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-amber-50/50 dark:bg-amber-500/5 rounded-lg border border-amber-200/60 dark:border-amber-500/20"
              >
                <div className="flex items-center gap-3">
                  <Badge variant="neutral">{doc.fileType}</Badge>
                  <div>
                    <div className="font-medium text-sm">{doc.fileName}</div>
                    <div className="text-xs text-muted-foreground">{formatTime(doc.createdAt)}</div>
                  </div>
                </div>
                <button
                  onClick={() => openDocumentView(doc)}
                  className="btn btn-ghost btn-sm"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointment Documents section */}
      <div className="space-y-2">
        {(poDocuments?.length ?? 0) > 0 && (
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Appointment Documents</h4>
        )}

        <div className="p-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
          <h4 className="font-semibold mb-3">Upload New Document</h4>
          <div className="flex flex-wrap gap-3">
            {['BOL', 'POD', 'Damage', 'Other'].map((type) => (
              <label key={type} className={`btn btn-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Paperclip className="w-3 h-3 mr-2" />}
                Upload {type}
                <input type="file" className="hidden" onChange={(e) => onFileUpload(e, type)} disabled={uploading} />
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {(documents?.length ?? 0) === 0 ? (
            <p className="text-center text-muted-foreground py-8">No appointment documents uploaded.</p>
          ) : (
            documents?.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800"
              >
                <div className="flex items-center gap-3">
                  <Badge variant="neutral">{doc.fileType}</Badge>
                  <div>
                    <div className="font-medium text-sm">{doc.fileName}</div>
                    <div className="text-xs text-muted-foreground">{formatTime(doc.createdAt)}</div>
                  </div>
                </div>
                <button
                  onClick={() => openDocumentView(doc)}
                  className="btn btn-ghost btn-sm"
                >
                  View
                </button>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
}
